import { z } from 'zod';

/**
 * The Lead Scanner scan wire contract (R5/R6, U4 of
 * plans/2026-08-25-001-lead-scanner-implementation-plan.md) — one shared
 * module so `api.ts` (backend validation + serialization) and the frontend
 * (`components/Home.tsx`, `components/ScanList.tsx`) read the exact same
 * shape, never two independently hand-maintained copies of the same wire
 * contract (repo convention: a cross-app/cross-boundary contract gets exactly
 * one implementation).
 *
 * Status vocabulary (Key Technical Decision 6): `processing` (photo intake,
 * fields not yet known) → `draft` (ready for review — blank-by-default or,
 * later, extracted) | `error` (unreadable) → `submitting` (the Clay routine
 * run is in flight — U9 of the round-2 plan) → `synced` (the run's
 * `Completion Status` was `Complete`) | `needs_review` (the run landed but
 * `Completion Status` was `partial` or `failed` — the lead may or may not be
 * fully in Salesforce; the review form pre-fills whatever the routine DID
 * find and the rep reviews/resubmits). `sourceType` gained `'text'` in U11
 * (round-2 plan) for the "Generate with text" intake path — a plain string
 * column, no migration needed for a new enum value; `needs_review` is the
 * same kind of plain-string addition to the `status` column.
 */

export const ScanStatusSchema = z.enum(['processing', 'draft', 'submitting', 'error', 'synced', 'needs_review']);
export type ScanStatus = z.infer<typeof ScanStatusSchema>;

export const ScanSourceTypeSchema = z.enum(['photo', 'manual', 'text']);
export type ScanSourceType = z.infer<typeof ScanSourceTypeSchema>;

/** Full row shape returned by every scan route (GET list, POST/PATCH echo). */
export const ScanSchema = z.object({
  id: z.string(),
  idempotencyKey: z.string(),
  status: ScanStatusSchema,
  sourceType: ScanSourceTypeSchema,
  name: z.string(),
  company: z.string(),
  email: z.string(),
  linkedin: z.string(),
  notes: z.string(),
  routeToGtme: z.boolean(),
  /** Rep-controlled override sent to the Lead Scanning Clay function's
   *  "Suppress from Follow Up" input (see `prisma/schema/lead-scanner.prisma`
   *  for the field's own rationale). */
  suppressFollowUp: z.boolean(),
  campaignId: z.string(),
  scannedByEmail: z.string(),
  matchedContactId: z.string().nullable(),
  sfdcCampaignMemberId: z.string().nullable(),
  sfdcResultUrl: z.string().nullable(),
  sfdcError: z.string().nullable(),
  /** The Clay routine's own `Final Contact Id` — persisted (Part 2) so a
   *  forced resubmit of an already-`synced`/`needs_review` row can prefer
   *  this over a blank `matchedContactId` rather than risk creating a
   *  duplicate SFDC Contact. */
  sfdcContactId: z.string().nullable(),
  /** The routine's own `Final Email` / `Final Linkedin URL` — persisted only
   *  to drive `needs_review`'s review-form pre-fill (fill-if-blank only);
   *  `null` on every scan the routine hasn't run against yet. */
  enrichedEmail: z.string().nullable(),
  enrichedLinkedin: z.string().nullable(),
  /** The routine's own `Final GTME Name` / `Final GTME Meeting Link` routing
   *  outputs, persisted (QA-fix batch) so a scan reopened later still shows
   *  the GTME owner and booking QR `buildPersistedSubmitResult` renders. */
  gtmeName: z.string().nullable(),
  bookingLink: z.string().nullable(),
  photoFilename: z.string().nullable(),
  /** Derived server-side from `photoContentType !== null`, never from
   *  `photoData` (which is globally omitted from reads, `_shared/db-client.ts`).
   *  `false` for manual/text scans, the LinkedIn-QR photo leg, and
   *  pre-existing scans (created before the photo-bytes column existed). */
  hasPhoto: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Scan = z.infer<typeof ScanSchema>;

export const ScansListResponseSchema = z.object({ scans: z.array(ScanSchema) });
export const ScanResponseSchema = z.object({ scan: ScanSchema });

/**
 * POST /scans body — creates a draft/processing row, upserted on
 * `idempotencyKey` (Key Technical Decision 7: the key is stamped client-side
 * at draft-creation time, not submit time, so a later Retry — U6 — reuses
 * it; the backend upserts so re-sending the same key never creates a second
 * row).
 */
export const ScanCreateSchema = z.object({
  idempotencyKey: z.string().min(1),
  sourceType: ScanSourceTypeSchema,
  status: ScanStatusSchema.default('draft'),
  campaignId: z.string().min(1),
  scannedByEmail: z.string().min(1),
  photoFilename: z.string().nullable().optional(),
  /**
   * A LinkedIn profile URL already known AT CREATION time (U12, R12 of
   * plans/2026-08-25-002-lead-scanner-round2-plan.md): the live-camera QR
   * scanner decodes the badge before any row exists, so its draft is born
   * with the identity already in it rather than created blank and
   * immediately PATCHed. Every other intake path omits it and gets the
   * column's `''` default.
   */
  linkedin: z.string().optional(),
  /**
   * Home's "search an existing campaign member → create a prefilled draft"
   * flow (Part 3 of the campaign-member-search extraction): the identity is
   * already known via `memberPrefillFields` (`campaign-members.ts`) BEFORE
   * the row exists, so — same rationale as `linkedin` above — the draft is
   * born with it rather than created blank and immediately PATCHed (a blank
   * "Untitled scan" row would otherwise flash into Home's Scans list
   * between the two writes). Every other intake path omits all four and gets
   * the column's own default (`''` for the three strings, `null` for
   * `matchedContactId`).
   */
  name: z.string().optional(),
  company: z.string().optional(),
  email: z.string().optional(),
  matchedContactId: z.string().nullable().optional(),
});
export type ScanCreateInput = z.infer<typeof ScanCreateSchema>;

/**
 * PATCH /scans/:id body — every field optional. Used this unit by the
 * "Scan badges" placeholder simulation (flipping a `processing` row to
 * `draft`/`error`) and, later, by extraction (U5) and the review form (U6).
 */
export const ScanPatchSchema = z
  .object({
    status: ScanStatusSchema,
    sourceType: ScanSourceTypeSchema,
    name: z.string(),
    company: z.string(),
    email: z.string(),
    linkedin: z.string(),
    notes: z.string(),
    routeToGtme: z.boolean(),
    suppressFollowUp: z.boolean(),
    campaignId: z.string().min(1),
    scannedByEmail: z.string().min(1),
    matchedContactId: z.string().nullable(),
    sfdcCampaignMemberId: z.string().nullable(),
    sfdcResultUrl: z.string().nullable(),
    sfdcError: z.string().nullable(),
    photoFilename: z.string().nullable(),
  })
  .partial();
export type ScanPatchInput = z.infer<typeof ScanPatchSchema>;

/**
 * Draft, error, or needs_review — the statuses that still need the rep's
 * attention. Originally the gate on Home's own scans list (R5); since the
 * QA-fix batch's Fix 1 (Home now shows every status, matching the Scans
 * page), this is used only to decide which rows offer a delete action
 * (`ScanListCard.tsx`/`ScansPage.tsx`) — a `synced`/`submitting`/`processing`
 * row is never deletable.
 */
export function isUnfinishedScan(scan: Pick<Scan, 'status'>): boolean {
  return scan.status === 'draft' || scan.status === 'error' || scan.status === 'needs_review';
}

/** Home/ScanList's `StatusBadge` mapping (plan U4, updated: draft→warning
 *  (yellow) — a draft may carry an advisory-only "partial detection"
 *  message (`PARTIAL_DETECTION_MESSAGE`) and yellow is the non-blocking
 *  signal for that, same as `needs_review` (draft and needs_review now
 *  render the same yellow, distinguished only by label text — expected,
 *  not a bug); error→error, processing/submitting→action, synced→success;
 *  needs_review→warning — a partial/failed run that landed but needs a
 *  rep's eyes before it can be trusted as fully in Salesforce). */
export const SCAN_STATUS_BADGE: Record<ScanStatus, 'neutral' | 'action' | 'success' | 'error' | 'warning'> = {
  draft: 'warning',
  error: 'error',
  processing: 'action',
  submitting: 'action',
  synced: 'success',
  needs_review: 'warning',
};

export const SCAN_STATUS_LABEL: Record<ScanStatus, string> = {
  draft: 'Draft',
  error: 'Error',
  processing: 'Processing',
  submitting: 'Syncing',
  synced: 'Synced',
  needs_review: 'Needs Review',
};

/**
 * Advisory copy for a normal `draft` scan whose extraction found no legible
 * badge/name-tag/business-card in the photo — this is NOT an error status
 * (see `scanPatchForExtraction` in `extraction.ts`, which maps `found: false`
 * to `{status: 'draft', sfdcError: PARTIAL_DETECTION_MESSAGE}` rather than
 * `status: 'error'`): every normal draft affordance (submit, delete, no
 * special routing) applies unchanged, the rep just gets a soft heads-up to
 * fill the form in by hand. `ScanReview.tsx` matches against this exact
 * string to decide when to render the yellow/warning advisory banner.
 */
export const PARTIAL_DETECTION_MESSAGE = 'Partial Detection - Please Fill The Form';

/**
 * Rep-facing copy for a submit that was throttled at the START (Clay submit
 * resilience plan §3.2/§3.4): the core execute route's bounded backoff loop
 * (`execution-handlers/clay-routine.ts`) ran out of attempts/budget while
 * Clay (or the pool) kept refusing at admission. Distinct from the generic
 * "Save photo and submit later please" copy — Retry has a real chance here
 * once the storm passes, unlike a genuine config/validation failure.
 * `submit-clay.ts`'s `fireLeadScanningExecute` selects this over the generic
 * copy when the execute route's error carries `code:
 * 'CLAY_ROUTINE_START_THROTTLED'`; `ScanReview.tsx` matches against this
 * exact string to skip the "fill the fields in by hand" suffix, which is
 * wrong advice for a throttled row.
 */
export const SUBMIT_THROTTLED_MESSAGE = 'Clay is busy right now — wait a minute, then Retry';

/**
 * Rep-facing copy for a `needs_review`/`error` row whose underlying execution
 * failed with `CLAY_ROUTINE_RESULT_UNAVAILABLE` (results-poll starvation fix,
 * plan §3.2/§4.2) — Clay was asked for long enough and never answered, so the
 * run's real outcome is genuinely unknown rather than a known failure. Never
 * conflated with the generic "Clay error" copy: a rep reading this should
 * check Salesforce for a possible duplicate before resubmitting, not just
 * retry.
 */
export const SUBMIT_RESULT_UNKNOWN_MESSAGE = 'Result unknown — check Salesforce before resubmitting';

/** Prototype's `scanRow()`/`scanDetail()` title fallback
 *  (docs/lead-scanner-prototype.html ~lines 1198, 1918) — ported verbatim,
 *  plus a draft carrying `PARTIAL_DETECTION_MESSAGE` (no legible badge found)
 *  falls back to "Needs Review" rather than "Untitled scan". */
export function scanTitle(scan: Pick<Scan, 'name' | 'status' | 'sfdcError'>): string {
  if (scan.name) return scan.name;
  if (scan.status === 'draft' && scan.sfdcError === PARTIAL_DETECTION_MESSAGE) return 'Needs Review';
  return scan.status === 'error' ? 'Unreadable badge' : 'Untitled scan';
}

/** Prototype's `srcLine()` (~line 925) — a manual draft has no photo or note
 *  behind it, so its provenance line IS "the rep typed it"; a text-intake
 *  draft (U11) similarly has no photo behind it. */
export function scanSourceLine(scan: Pick<Scan, 'sourceType' | 'photoFilename'>): string {
  if (scan.sourceType === 'manual') return 'Added by hand';
  if (scan.sourceType === 'text') return 'Generated from text';
  return `From photo · ${scan.photoFilename ?? 'unknown file'}`;
}

/** Poll cadence while any row in the list is still `processing` (plan U5:
 *  "simple polling of GET /scans while any row is 'processing' ... stop when
 *  none"). Exported (not local to `useScanApi.ts`) so `scansListPollInterval`
 *  below is the one definition of this cadence — SSE is not available to
 *  mini-apps. */
export const PROCESSING_POLL_MS = 2000;

/** Poll cadence while a fired submission is still `submitting`/`pending` —
 *  the same fixed 5s Flow's own Requests feed uses
 *  (`REQUESTS_POLL_INTERVAL_MS`, `EnrollQueue/requests-hooks.ts`), the
 *  closest precedent for an async `action_executions` run. Deliberately NOT
 *  the 2s `PROCESSING_POLL_MS` above uses: extraction resolves in seconds, a
 *  Clay routine can take minutes. */
export const SUBMIT_STATUS_POLL_MS = 5000;

/**
 * List-poll cadence covering BOTH in-flight statuses (round-2 bug fix, Part
 * D of the submitting-scan-gets-stranded fix). Before this, `GET /scans`'s
 * own poll only watched for `processing` rows — a `submitting` row had NO
 * observer at all once the submit-response modal was closed, which was half
 * of why a submitted scan could get permanently stranded: nothing was ever
 * asking `GET /scans/:id/submit-status` for it again. `GET /scans` now
 * finalizes `submitting` rows server-side on every read (`api.ts`'s
 * `finalizeSubmittingRows`), and this is what keeps the list actually
 * re-reading while one is in flight.
 *
 * `processing` wins when both are present — it resolves faster and is more
 * likely to be the thing a rep is actively staring at. `false` stops the
 * poll once nothing in the list is in flight.
 */
export function scansListPollInterval(scans: Pick<Scan, 'status'>[]): number | false {
  if (scans.some((s) => s.status === 'processing')) return PROCESSING_POLL_MS;
  if (scans.some((s) => s.status === 'submitting')) return SUBMIT_STATUS_POLL_MS;
  return false;
}

/**
 * `useAllScansQuery`'s variant of the poll rule: the unfiltered Scans-page
 * list contains every rep's rows, but only the VIEWER's own in-flight rows
 * should keep the page polling — the server sweep deliberately never
 * finalizes another rep's row, so a foreign abandoned `processing`/
 * `submitting` row would otherwise keep every open Scans page polling at
 * the short interval forever.
 */
export function scansListPollIntervalFor(
  viewerEmail: string | null,
  scans: Pick<Scan, 'status' | 'scannedByEmail'>[],
): number | false {
  if (viewerEmail === null) return false;
  return scansListPollInterval(scans.filter((s) => s.scannedByEmail === viewerEmail));
}

/**
 * POST /scans/:id/submit request body (R10/R11/R12, U6) — mirrors the
 * (unexported) `ScanSubmitSchema` in `api.ts` field-for-field. Declared here
 * rather than imported from `api.ts` because that module is backend-only
 * (Express, Prisma) — not bundle-safe for the frontend — and its schema is
 * not exported. This is the frontend's own outbound-payload shape, not a
 * second implementation of the server's inbound validation: the server still
 * independently `safeParse`s whatever JSON actually arrives.
 *
 * A plain interface, not a zod schema — it describes a payload THIS app
 * constructs and fully controls before sending, not something crossing in
 * from outside, so there is no trust boundary here to parse against (unlike
 * every response shape below, which does cross one and is `safeParse`d).
 */
export interface ScanSubmitInput {
  idempotencyKey: string;
  sourceType: ScanSourceType;
  name: string;
  company: string;
  email: string;
  linkedin: string;
  notes: string;
  routeToGtme: boolean;
  suppressFollowUp: boolean;
  campaignId: string;
  scannedByEmail: string;
  matchedContactId: string | null;
  /**
   * "Update in Salesforce" (Part 2) — re-invokes the SAME Clay routine for a
   * scan that is already `synced`. Omitted/`false` everywhere else,
   * including Retry and "Add note & resend" (both of those already work off
   * a non-`synced` row, so they never need this). The server's claim CAS
   * only lets `synced` be re-claimed when this is explicitly `true` — never
   * by accident via the default submit path.
   */
  forceResubmit?: boolean;
}

/**
 * Outcome of one submission attempt, as the POLL route reports it (U9 of
 * plans/2026-08-25-002-lead-scanner-round2-plan.md).
 *
 * Two arms, because the Clay function is async by construction — it answers
 * `202 in_progress` and the real fields arrive on a later results fetch
 * (`docs/lead-scanner-clay-function-contract.md`), so "no outcome yet" is a
 * first-class state the rep sees rather than a hidden intermediate the server
 * blocks on:
 *
 * - `{pending: true}` — the `action_executions` row is still
 *   `pending`/`dispatching`. The scan stays `submitting`.
 * - `{pending: false, ...}` — terminal. `status` is the function's own
 *   `Completion Status` (`Complete` | `partial` | `failed`), and every other
 *   field is nullable because a `partial` run populates the person but not
 *   the routing, and a `failed` run populates neither.
 *
 * `routing` mirrors the contract's five routing outputs verbatim (decision,
 * GTME name/email, booking link, follow-up explainer). `Final Followup
 * Explainer` stays prose — "routed to ClayDR" lives inside it by explicit
 * decision and is never broken out into its own field.
 *
 * `enriched` mirrors the routine's own `Full Name` / `Company Name` /
 * `Final Email` / `Final Linkedin URL` input-echo-and-output fields — present
 * on every terminal arm (Complete, partial, and failed alike) so the shape is
 * total; only `enrichedEmail`/`enrichedLinkedin` off the persisted scan row
 * (not this transient poll result) drive the `needs_review` review-form
 * pre-fill, but the poll result carries the full set for completeness.
 *
 * Declared here (not imported from `submit-clay.ts`) because the frontend
 * never imports backend code: this is the frontend's own `safeParse`-checked
 * read of the response, per the repo's schema-first data-boundary convention.
 */
export const ScanSubmitResultSchema = z.union([
  z.object({ pending: z.literal(true) }),
  z.object({
    pending: z.literal(false),
    status: z.enum(['Complete', 'partial', 'failed']),
    contactId: z.string().nullable(),
    campaignMemberId: z.string().nullable(),
    crmUrl: z.string().nullable(),
    routing: z.object({
      decision: z.string().nullable(),
      gtmeName: z.string().nullable(),
      gtmeEmail: z.string().nullable(),
      bookingLink: z.string().nullable(),
      followupExplainer: z.string().nullable(),
    }),
    enriched: z.object({
      fullName: z.string().nullable(),
      company: z.string().nullable(),
      email: z.string().nullable(),
      linkedin: z.string().nullable(),
    }),
    /** Rep-facing failure text for a `failed` run (the execution's
     *  `delivery_error.message`, or the reason its result was unreadable).
     *  `null` on every non-failed run. */
    error: z.string().nullable().default(null),
  }),
]);
export type ScanSubmitResult = z.infer<typeof ScanSubmitResultSchema>;

/**
 * `POST /scans/:id/submit` response. No result: the submit call only FIRES
 * the run (U9 step 3), so all it can hand back is the persisted row plus the
 * `action_executions` id the poll route reads. The rep's modal opens in the
 * pending state on this response and fills in from the poll.
 */
export const ScanSubmitResponseSchema = z.object({
  scan: ScanSchema,
  executionId: z.string(),
});
export type ScanSubmitResponse = z.infer<typeof ScanSubmitResponseSchema>;

/** `GET /scans/:id/submit-status` response (U9 step 4) — the row as it stands
 *  after any terminal finalization, plus this poll's read of the run. */
export const ScanSubmitStatusResponseSchema = z.object({
  scan: ScanSchema,
  result: ScanSubmitResultSchema,
});
export type ScanSubmitStatusResponse = z.infer<typeof ScanSubmitStatusResponseSchema>;

/**
 * `POST /scans/from-text` request body (U11, R11) — a rep-pasted blob of
 * freeform notes. A plain interface, not a zod schema, for the same reason
 * as `ScanSubmitInput` above: this app constructs and fully controls the
 * payload before sending it, so there is no trust boundary here to parse
 * against — the server still independently `safeParse`s whatever JSON
 * actually arrives.
 */
export interface TextIntakeInput {
  text: string;
  campaignId: string;
  scannedByEmail: string;
}

/**
 * `POST /scans/from-text` response — the extraction is synchronous (no
 * `processing` intermediate state, U11's Key Technical Decision 11), so the
 * response IS the finished set of draft rows: zero when the text named no
 * leads, N otherwise, in extraction order.
 */
export const TextIntakeResponseSchema = z.object({ scans: z.array(ScanSchema) });
export type TextIntakeResponse = z.infer<typeof TextIntakeResponseSchema>;
